import React, { useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from 'sonner';
import { getPlaceholderConfig } from './services/api';
import { initPlaceholderImages } from './utils/constants';

class ErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('App error boundary:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Algo salió mal</h2>
            <p className="text-gray-500 mb-4">La sesión pudo haber expirado.</p>
            <button
              onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login'; }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Ir al inicio de sesión
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import DashboardHome from './pages/dashboard/DashboardHome';
import Productos from './pages/dashboard/Productos';
import Inventario from './pages/dashboard/Inventario';
import Sucursales from './pages/dashboard/Sucursales';
import CajasPOS from './pages/dashboard/CajasPOS';
import Reportes from './pages/dashboard/Reportes';
import Configuracion from './pages/dashboard/Configuracion';
import Soporte from './pages/dashboard/Soporte';
import Facturacion from './pages/dashboard/Facturacion';
import Cajeros from './pages/dashboard/Cajeros';
import Repartidores from './pages/dashboard/Repartidores';
import EntregasRemisiones from './pages/dashboard/EntregasRemisiones';
import Clientes from './pages/dashboard/Clientes';
import Cotizaciones from './pages/dashboard/Cotizaciones';
import Ventas from './pages/dashboard/Ventas';
import FacturacionManual from './pages/dashboard/FacturacionManual';
import PlanComplementos from './pages/dashboard/PlanComplementos';
import DeliveryApp from './pages/DeliveryApp';
import DeliveryBusinessApp from './pages/DeliveryBusinessApp';
import Autofacturacion from './pages/Autofacturacion';
import PublicQuoteView from './pages/PublicQuoteView';
import POSMain from './pages/pos/POSMain';
import SuperAdminHome from './pages/superadmin/SuperAdminHome';
import Negocios from './pages/superadmin/Negocios';
import Tickets from './pages/superadmin/Tickets';
import Categorias from './pages/superadmin/Categorias';
import BannersManagement from './pages/superadmin/BannersManagement';
import LandingLayout from './pages/superadmin/LandingLayout';
import ConfiguracionEmail from './pages/superadmin/ConfiguracionEmail';
import SuperAdminConfiguracion from './pages/superadmin/SuperAdminConfiguracion';
import SuperAdminLicenciasPOS from './pages/superadmin/LicenciasPOS';
import LicenciaGestion from './pages/superadmin/LicenciaGestion';
import FacturacionAdmin from './pages/superadmin/FacturacionAdmin';
import VentasLicencias from './pages/superadmin/VentasLicencias';
import ComprasWeb from './pages/superadmin/ComprasWeb';
import SyncDashboard from './pages/superadmin/SyncDashboard';
import DeliveryHubAdmin from './pages/superadmin/DeliveryHubAdmin';
import GigDriversAdmin from './pages/superadmin/GigDriversAdmin';
import GigSettlements from './pages/superadmin/GigSettlements';
import WhatsAppAccounts from './pages/superadmin/whatsapp/WhatsAppAccounts';
import WhatsAppInbox from './pages/superadmin/whatsapp/WhatsAppInbox';
import WhatsAppAutoReplies from './pages/superadmin/whatsapp/WhatsAppAutoReplies';
import WhatsAppTemplates from './pages/superadmin/whatsapp/WhatsAppTemplates';
import WhatsAppQuickReplies from './pages/superadmin/whatsapp/WhatsAppQuickReplies';
import WhatsAppNotificationLog from './pages/superadmin/whatsapp/WhatsAppNotificationLog';
import WhatsAppContacts from './pages/superadmin/whatsapp/WhatsAppContacts';
import WhatsAppEventSettings from './pages/superadmin/whatsapp/WhatsAppEventSettings';
import TiposNegocio from './pages/superadmin/TiposNegocio';
import MasterCategories from './pages/superadmin/MasterCategories';
import SuperadminUsers from './pages/superadmin/SuperadminUsers';
import PassPayouts from './pages/superadmin/PassPayouts';
import PassSettlements from './pages/superadmin/PassSettlements';
import PassReports from './pages/superadmin/PassReports';
import PassGuides from './pages/superadmin/PassGuides';
import DashboardLicenciasPOS from './pages/dashboard/LicenciasPOS';
import MenuDigital from './pages/dashboard/MenuDigital';
import Secciones from './pages/dashboard/Secciones';
import ComprarAddons from './pages/dashboard/ComprarAddons';
import ListasPrecios from './pages/dashboard/ListasPrecios';
import CierresTurno from './pages/dashboard/CierresTurno';
import SyncLog from './pages/dashboard/SyncLog';
import AjustesInventario from './pages/dashboard/AjustesInventario';
import AnalisisVentas from './pages/dashboard/AnalisisVentas';
import SeguimientoEntregas from './pages/dashboard/SeguimientoEntregas';
import DeliveryHub from './pages/dashboard/DeliveryHub';
import MenuPublic from './pages/menu/MenuPublic';

// Marketplace Pages
import MarketplaceLanding from './pages/marketplace/Landing';
import MarketplaceSearch from './pages/marketplace/Search';
import ProductDetail from './pages/marketplace/ProductDetail';
import StoreProfile from './pages/marketplace/StoreProfile';
import BranchProfile from './pages/marketplace/BranchProfile';
import { CartPage, CheckoutPage, MyOrdersPage } from './pages/marketplace/CartCheckout';
import CustomerAuth from './pages/marketplace/CustomerAuth';
import CustomerDashboard from './pages/marketplace/CustomerDashboard';
import FavoritesPage from './pages/marketplace/FavoritesPage';
import { AcercaPage, ContactoPage, ComoFuncionaPage, FAQPage, PrivacidadPage, TerminosPage, PreciosPage } from './pages/marketplace/StaticPages';
import ComandaProPage from './pages/marketplace/ComandaPro';
import StoresSearch from './pages/marketplace/StoresSearch';
import CategoriesPage from './pages/marketplace/CategoriesPage';
import ComidaLanding from './pages/marketplace/ComidaLanding';
import RestaurantDetail from './pages/marketplace/RestaurantDetail';
import SuperInteligente from './pages/marketplace/SuperInteligente';
import SuperCheckout from './pages/marketplace/SuperCheckout';
import SuperOrderTracking from './pages/marketplace/SuperOrderTracking';
import ExperiencesPage from './pages/marketplace/ExperiencesPage';

// Pass — afiliados, ofertas, check-in
import PassLanding from './pages/pass/PassLanding';
import AffiliateProfile from './pages/pass/AffiliateProfile';
import OfferDetail from './pages/pass/OfferDetail';
import TicketView from './pages/pass/TicketView';
import AffiliateDashboard from './pages/pass/AffiliateDashboard';
import PassDashboard from './pages/pass/business/PassDashboard';
import PassOfferForm from './pages/pass/business/PassOfferForm';
import PassScanner from './pages/pass/business/PassScanner';
import AffiliateAttributionTracker from './components/AffiliateAttributionTracker';
import GuideView from './pages/pass/GuideView';

import './App.css';

function App() {
  useEffect(() => {
    getPlaceholderConfig()
      .then(res => initPlaceholderImages(res.data))
      .catch(() => {});
  }, []);

  return (
    <ErrorBoundary>
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <AffiliateAttributionTracker />
        <Routes>
          {/* ==================== PASS (afiliados, promos, check-in) ==================== */}
          <Route path="/pass" element={<PassLanding />} />
          <Route path="/pass/affiliate" element={<AffiliateDashboard />} />
          <Route path="/pass/oferta/:offerSlug" element={<OfferDetail />} />
          <Route path="/pass/guia/:slug" element={<GuideView />} />
          <Route path="/pass/ticket/:qrToken" element={<TicketView />} />
          <Route path="/pass/:affiliateSlug" element={<AffiliateProfile />} />
          <Route
            path="/dashboard/pass"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <PassDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/pass/ofertas/nueva"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <PassOfferForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/pass/ofertas/:offerId"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <PassOfferForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/pass/scanner"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager', 'cashier']}>
                <PassScanner />
              </ProtectedRoute>
            }
          />

          {/* ==================== MARKETPLACE PUBLIC ROUTES ==================== */}
          <Route path="/" element={<MarketplaceLanding />} />
          <Route path="/buscar" element={<MarketplaceSearch />} />
          <Route path="/categorias" element={<CategoriesPage />} />
          <Route path="/tiendas" element={<StoresSearch />} />
          <Route path="/producto/:productId" element={<ProductDetail />} />
          <Route path="/tienda/:storeId" element={<StoreProfile />} />
          <Route path="/tienda/:storeId/sucursal/:branchId" element={<BranchProfile />} />
          <Route path="/carrito" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/mis-pedidos" element={<MyOrdersPage />} />
          <Route path="/mi-cuenta" element={<CustomerDashboard />} />
          <Route path="/favoritos" element={<FavoritesPage />} />
          <Route path="/auth" element={<CustomerAuth />} />
          
          {/* Static Pages */}
          <Route path="/acerca" element={<AcercaPage />} />
          <Route path="/contacto" element={<ContactoPage />} />
          <Route path="/como-funciona" element={<ComoFuncionaPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacidad" element={<PrivacidadPage />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/precios" element={<PreciosPage />} />
          <Route path="/comanda-pro" element={<ComandaProPage />} />

          {/* ==================== FOOD MARKETPLACE ==================== */}
          <Route path="/comida" element={<ComidaLanding />} />
          <Route path="/comida/restaurante/:businessId" element={<RestaurantDetail />} />

          {/* ==================== EXPERIENCIAS PASS (público) ==================== */}
          <Route path="/experiencias" element={<ExperiencesPage />} />

          {/* ==================== SUPER INTELIGENTE ==================== */}
          <Route path="/super" element={<SuperInteligente />} />
          <Route path="/super/checkout" element={<SuperCheckout />} />
          <Route path="/super/pedido/:orderId" element={<SuperOrderTracking />} />

          {/* ==================== DIGITAL MENU PUBLIC ROUTE ==================== */}
          <Route path="/menu/:slug" element={<MenuPublic />} />

          {/* ==================== BUSINESS AUTH ROUTES ==================== */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/facturacion/:ticketId" element={<Autofacturacion />} />
          <Route path="/q/:token" element={<PublicQuoteView />} />

          {/* Delivery App - Uses its own authentication */}
          <Route path="/repartidor" element={<DeliveryApp />} />

          {/* Delivery Negocios (remisiones) - PWA propia con session_token */}
          <Route path="/delivery_negocios" element={<DeliveryBusinessApp />} />

          {/* POS System - Uses its own authentication (terminal hash + PIN) */}
          <Route path="/pos" element={<POSMain />} />

          {/* Business Owner Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <DashboardHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/productos"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Productos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventario"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Inventario />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/sucursales"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Sucursales />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/cajas"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <CajasPOS />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/cajeros"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Cajeros />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reportes"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reportes/ventas"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reportes/inventario"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reportes/caja"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reportes/top-productos"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reportes/marketplace"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reportes/cotizaciones"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/configuracion"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Configuracion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/soporte"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Soporte />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/facturacion"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Facturacion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/facturacion-manual"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <FacturacionManual />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/clientes"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Clientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/ventas"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Ventas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/cotizaciones"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Cotizaciones />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/repartidores"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Repartidores />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/plan"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <PlanComplementos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/secciones"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <Secciones />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/menu-digital"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <MenuDigital />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/comprar-addons"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <ComprarAddons />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/listas-precios"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <ListasPrecios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/cierres-turno"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <CierresTurno />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/sync-log"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <SyncLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/ajustes-inventario"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <AjustesInventario />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/analisis-ventas"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <AnalisisVentas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/seguimiento-entregas"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <SeguimientoEntregas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/entregas-remisiones"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <EntregasRemisiones />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/licencias-pos"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <DashboardLicenciasPOS />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/delivery-hub"
            element={
              <ProtectedRoute allowedRoles={['business_owner', 'branch_manager']}>
                <DeliveryHub />
              </ProtectedRoute>
            }
          />

          {/* SuperAdmin Dashboard */}
          <Route path="/superadmin" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="dashboard"><SuperAdminHome /></ProtectedRoute>} />
          <Route path="/superadmin/negocios" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="negocios"><Negocios /></ProtectedRoute>} />
          <Route path="/superadmin/tickets" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="tickets"><Tickets /></ProtectedRoute>} />
          <Route path="/superadmin/categorias" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="categorias"><Categorias /></ProtectedRoute>} />
          <Route path="/superadmin/banners" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="banners"><BannersManagement /></ProtectedRoute>} />
          <Route path="/superadmin/landing-layout" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="landing"><LandingLayout /></ProtectedRoute>} />
          <Route path="/superadmin/email" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="email"><ConfiguracionEmail /></ProtectedRoute>} />
          <Route path="/superadmin/configuracion" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="configuracion"><SuperAdminConfiguracion /></ProtectedRoute>} />
          <Route path="/superadmin/licencias-pos" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="licencias"><SuperAdminLicenciasPOS /></ProtectedRoute>} />
          <Route path="/superadmin/licencias-pos/:serial/gestion" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="licencias"><LicenciaGestion /></ProtectedRoute>} />
          <Route path="/superadmin/facturacion" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="facturacion_admin"><FacturacionAdmin /></ProtectedRoute>} />
          <Route path="/superadmin/tipos-negocio" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="tipos_negocio"><TiposNegocio /></ProtectedRoute>} />
          <Route path="/superadmin/categorias-maestras" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="categorias_maestras"><MasterCategories /></ProtectedRoute>} />
          <Route path="/superadmin/ventas-licencias" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="licencias"><VentasLicencias /></ProtectedRoute>} />
          <Route path="/superadmin/compras" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="compras"><ComprasWeb /></ProtectedRoute>} />
          <Route path="/superadmin/sync-dashboard" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="sync"><SyncDashboard /></ProtectedRoute>} />
          <Route path="/superadmin/delivery-hub" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="delivery_hub"><DeliveryHubAdmin /></ProtectedRoute>} />
          <Route path="/superadmin/gig-drivers" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="gig_drivers"><GigDriversAdmin /></ProtectedRoute>} />
          <Route path="/superadmin/gig-settlements" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="gig_drivers"><GigSettlements /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppAccounts /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp/inbox" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppInbox /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp/auto-replies" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppAutoReplies /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp/templates" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppTemplates /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp/quick-replies" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppQuickReplies /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp/contacts" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppContacts /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp/event-settings" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppEventSettings /></ProtectedRoute>} />
          <Route path="/superadmin/whatsapp/notifications" element={<ProtectedRoute allowedRoles={['superadmin']} requiredPermission="whatsapp"><WhatsAppNotificationLog /></ProtectedRoute>} />
          <Route path="/superadmin/usuarios-admin" element={<ProtectedRoute allowedRoles={['superadmin']}><SuperadminUsers /></ProtectedRoute>} />
          <Route path="/superadmin/pass/payouts" element={<ProtectedRoute allowedRoles={['superadmin']}><PassPayouts /></ProtectedRoute>} />
          <Route path="/superadmin/pass/settlements" element={<ProtectedRoute allowedRoles={['superadmin']}><PassSettlements /></ProtectedRoute>} />
          <Route path="/superadmin/pass/reportes" element={<ProtectedRoute allowedRoles={['superadmin']}><PassReports /></ProtectedRoute>} />
          <Route path="/superadmin/pass/guias" element={<ProtectedRoute allowedRoles={['superadmin']}><PassGuides /></ProtectedRoute>} />

          {/* Default Route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
